import { useCallback, useEffect, useMemo, useState } from "react";
import { CarouselWoList } from "../models/ResponseModels/CarouselResponse";
import ErrorsString from "../ErrorsString";
import Spinner from "./Spinner";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  EffectCoverflow,
  Pagination,
  Autoplay,
  Navigation,
} from "swiper/modules";
import Alert from "@mui/material/Alert";
import config from "../config";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import TestLogin from "../models/TestLogin";
import { GetCarouselPublicWoModel } from "../models/GetCarouselPublicWoModel";
import { useNavigate } from "react-router-dom";
import CustomPagination from "./CustomPagination";
import CarouselSlide from "./CarouselSlide";

interface Props {
  idPage: string;
}

export const INITIAL_SLIDE = 0;
export const SLIDES_PER_VIEW = 5;
export const EFFECT_COVERFLOW_SWIPER = {
  slideShadows: false,
  rotate: 3,
  scale: 0.75,
  stretch: -100,
  depth: 400,
};

export default function CarouselHome(props: Props) {
  let [carouselWo, setCarouselWo] = useState<CarouselWoList[]>([]);
  let [alertMessage, setAlertMessage] = useState<string>("");
  let [showAlert, setShowAlert] = useState<boolean>(false);
  const [spinnerVisibility, setSpinnerVisibility] = useState(false);
  const navigate = useNavigate();
  let [isLogged, setIsLogged] = useState<boolean>(false);

  const setErrorResponse = useCallback((message: string) => {
    setShowAlert(true);
    setAlertMessage(message);
    setSpinnerVisibility(false);
  }, []);

  const validateResponse = useCallback((response: any): string | null => {
    const responseData = response?.data;
    const statusCode = response?.status;

    if (responseData === undefined || statusCode !== 200) {
      return ErrorsString.errorMessageGeneric;
    }
    if (responseData.code !== "0") {
      return responseData.message;
    }
    if (responseData.carouselWoList.length === 0) {
      return ErrorsString.errorMessageCarousel;
    }
    return null;
  }, []);

  const getCarouselWo = useCallback(
    (isLoggedIn: boolean) => {
      window.scrollTo(0, 0);
      setSpinnerVisibility(true);

      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get("token") || null;

      (async () => {
        const response = await GetCarouselPublicWoModel(
          props.idPage,
          tokenFromUrl,
        );
        const errorMessage = validateResponse(response);

        if (errorMessage) {
          setErrorResponse(errorMessage);
          return;
        }

        // After validation, response is guaranteed to have data
        const validResponse = response as any;
        if (validResponse?.data) {
          setCarouselWo(validResponse.data.carouselWoList);
          setShowAlert(false);
          setAlertMessage("");
        }
        setSpinnerVisibility(false);
      })();
    },
    [props.idPage, validateResponse, setErrorResponse],
  );

  useEffect(() => {
    (async () => {
      const isLoggedIn = await TestLogin();
      //isLoggedIn = true;
      setIsLogged(isLoggedIn);
      getCarouselWo(isLoggedIn);
    })();
  }, [getCarouselWo]);

  const safeSlides = useMemo(() => {
    if (carouselWo.length > SLIDES_PER_VIEW) {
      return carouselWo;
    } else {
      return [...carouselWo, ...carouselWo];
    }
  }, [carouselWo]);

  const getImageUrl = useCallback((item: CarouselWoList) => {
    const hasImages = item.images?.length > 0;
    return hasImages
      ? config.cloudfrontImgUrl +
          item.images[0].imageUrl +
          config.showImagesQuery
      : config.placeholderImageUrl;
  }, []);

  const getImageStyle = useCallback(
    (isActive: boolean) => ({
      filter: isActive ? "grayscale(0%)" : "grayscale(75%)",
      transition: "filter 500ms",
    }),
    [],
  );

  const openWorkOrderPage = useCallback(
    (uuid: string, imageUrl: string) => {
      if (isLogged) {
        const parameters = {
          previewImageUrl: imageUrl,
        };
        navigate("/details/" + uuid, { state: JSON.stringify(parameters) });
      }
    },
    [isLogged, navigate],
  );

  const renderErrorAlert = useCallback(
    () => (
      <Alert
        severity="error"
        data-test-id="carousel-error"
        style={{
          display: showAlert ? "flex" : "none",
        }}
      >
        {alertMessage}
      </Alert>
    ),
    [showAlert, alertMessage],
  );

  return (
    <>
      <Spinner visibility={spinnerVisibility} />
      <div id="carousel-container" className="container mb-4">
        <div className="relative w-full h-[450px] overflow-hidden">
          {safeSlides.length > 0 ? (
            <Swiper
              id="carousel-swiper"
              spaceBetween={0}
              slidesPerView={3}
              initialSlide={INITIAL_SLIDE}
              navigation={true}
              pagination={false}
              loop={true}
              centeredSlides={true}
              autoplay={{
                delay: 10000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              coverflowEffect={EFFECT_COVERFLOW_SWIPER}
              modules={[Pagination, EffectCoverflow, Autoplay, Navigation]}
              breakpoints={{
                768: {
                  slidesPerView: SLIDES_PER_VIEW,
                },
              }}
              className="absolute top-0 left-1/2 bottom-0 inset-x-2/4 -translate-x-1/2 max-w-[1920px]  md:w-[1920px]"
              style={{
                position: "absolute",
              }}
            >
              {safeSlides.map((item, index) => (
                <SwiperSlide key={`${item.workorderId}-${index}`}>
                  {({ isActive, isVisible }) => (
                    <CarouselSlide
                      item={item}
                      isActive={isActive}
                      isVisible={isVisible}
                      onSlideClick={openWorkOrderPage}
                      getImageUrl={getImageUrl}
                      getImageStyle={getImageStyle}
                    />
                  )}
                </SwiperSlide>
              ))}

              <CustomPagination data={carouselWo} />
            </Swiper>
          ) : (
            renderErrorAlert()
          )}
        </div>
      </div>
    </>
  );
}
